<template>
  <span class="zm-radio">
    <label
      class="zm-radio-wrap"
      :class="[
        { 'is-disabled': isDisabled },
        { 'is-focus': focus },
        { 'is-checked': model === label }
      ]"
    >
      <input
        class="zm-radio__original"
        :value="label"
        type="radio"
        v-model="model"
        @focus="focus = true"
        @blur="focus = false"
        @change="handleChange"
        :name="radioName"
        :disabled="isDisabled"
        :aria-checked="(model===label).toString()"
        :tabIndex="tabIndex"
        :aria-describedby="describedId"
      >
      <span class="zm-radio__inner"></span>
      <span class="zm-radio__label">
        <slot></slot>
        <template v-if="!$slots.default">{{label}}</template>
        <span class="zm-radio__suffix" v-if="$slots.suffix">
          <slot name="suffix"></slot>
        </span>
      </span>
    </label>
  </span>
</template>
<script>
  import Emitter from '../../mixins/emitter';

  export default {
    name: 'ZmRadio',

    mixins: [Emitter],

    inject: {
      ZmForm: {
        default: ''
      },

      ZmFormItem: {
        default: ''
      }
    },

    componentName: 'ZmRadio',

    props: {
      value: {},
      label: {},
      disabled: Boolean,
      name: String
    },

    data() {
      return {
        focus: false
      };
    },
    computed: {
      isGroup() {
        let parent = this.$parent;
        while (parent) {
          if (parent.$options.componentName !== 'ZmRadioGroup') {
            parent = parent.$parent;
          } else {
            this._radioGroup = parent;
            return true;
          }
        }
        return false;
      },
      model: {
        get() {
          return this.isGroup ? this._radioGroup.value : this.value;
        },
        set(val) {
          if (this.isGroup) {
            this.dispatch('ZmRadioGroup', 'input', [val]);
          } else {
            this.$emit('input', val);
          }
        }
      },
      isDisabled() {
        return this.isGroup
          ? this._radioGroup.disabled || this.disabled || (this.ZmForm || {}).disabled
          : this.disabled || (this.ZmForm || {}).disabled;
      },
      tabIndex() {
        return !this.isDisabled ? (this.isGroup ? (this.model === this.label ? 0 : -1) : 0) : -1;
      },
      radioName() {
        return this.isGroup ? (this._radioGroup.name || this.name) : this.name
      },
      describedId() {
        if (this._radioGroup && this._radioGroup.enterSelect && this.model !== this.label) {
          return this._radioGroup.uid
        }
      }
    },

    methods: {
      handleChange() {
        this.$nextTick(() => {
          this.$emit('change', this.model);
          this.isGroup && this.dispatch('ZmRadioGroup', 'handleChange', this.model);
        });
      }
    }
  };
</script>

<style lang="scss">
  @import "../../scss/mixins/mixins";
  @import "../../scss/mixins/utils";
  @import "../../scss/common/var";

  @include b(radio) {
    color: $--radio-color;
    position: relative;
    display: inline-block;
    outline: none;
    font-size: inherit;
    margin-right: 30px;
    &:last-of-type {
      margin-right: 0;
    }

    .zm-radio-wrap {
      position: relative;
      display: inline-block;
      cursor: default;
      padding: 4px 0 4px 24px;
      min-height: 20px;
      line-height: 24px;
      margin-bottom: 0;
      @include when(disabled) {
        color: $--color-text-disabled;
        cursor: not-allowed;
      }
    }

    @include e(original) {
      opacity: 0;
      outline: none;
      position: absolute;
      z-index: -1;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      margin: 0;

      &:disabled + .zm-radio__inner {
        &::before {
          background-color: $--radio-disabled-input-fill;
          border-color: $--radio-disabled-input-border-color;
          opacity: 0.35;
          cursor: not-allowed;
        }

        &::after {
          cursor: not-allowed;
          border-color: $--radio-disabled-checked-icon-color;
        }
      }
      &:checked + .zm-radio__inner {
        &::before {
          border-color: $--radio-checked-input-border-color;
          background-color: $--radio-checked-input-border-color;
        }

        &::after {
          transform: scale(1);
        }
      }
      &:disabled:checked + .zm-radio__inner {
        &::before {
          background-color: $--radio-checked-input-border-color;
          border-color: $--radio-checked-input-border-color;
        }

        &::after {
          transform: scale(1);
        }
      }
    }
    @include e(inner) {
      width: $--radio-input-width;
      height: $--radio-input-height;
      position: absolute;
      left: 0;
      margin-right: 8px;
      top: 6px;
      &::before {
        position: absolute;
        left: 0;
        top: 50%;
        margin-top: - $--radio-input-height / 2 + 1;
        border-radius: $--radio-input-border-radius;
        content: "";
        border: $--radio-input-border;
        border-radius: $--radio-input-border-radius;
        width: $--radio-input-width;
        height: $--radio-input-height;
        background-color: $--radio-input-fill;
        cursor: pointer;
        box-sizing: border-box;
        position: absolute;
        left: 0;
      }
      &::after {
        width: $--radio-input-width - 8;
        height: $--radio-input-width - 8;
        border-radius: $--radio-input-border-radius;
        background-color: $--color-white;
        content: "";
        position: absolute;
        left: 4px;
        top: 5px;
        transform: scale(0);
        transition: transform .15s ease-in;
      }
    }

    @include e(label) {
      font-size: inherit;
      flex: 1;
      &:hover {
        border-color: $--radio-input-border-color-hover;
      }

    }
  }

  .is-keyboard-event .zm-radio__original:focus:not(:active):not(.is-disabled) + .zm-radio__inner:before {
    box-shadow: 0 0 0 2px #fff, 0 0 0 4px $--color-primary;
  }
</style>
